"use client";

import Image from "next/image";
import React, { useState, useEffect, useCallback } from "react";

import { Modal, ModalContent, Button } from "@nextui-org/react";
import cn from "clsx";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Download,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

import { createLogger } from "@/utils/logger";

const logger = createLogger("PropertyComments:ImageGalleryModal");

interface ImageItem {
  _id: string;
  url: string;
  originalName?: string;
  filename?: string;
  size?: number;
}

interface ImageGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: ImageItem[];
  initialIndex?: number;
  className?: string;
}

export const ImageGalleryModal: React.FC<ImageGalleryModalProps> = ({
  isOpen,
  onClose,
  images,
  initialIndex = 0,
  className,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [imageError, setImageError] = useState<Record<string, boolean>>({});

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(Math.max(0, Math.min(initialIndex, images.length - 1)));
      setIsZoomed(false);
      setImageError({});
    }
  }, [isOpen, initialIndex, images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowLeft":
          e.preventDefault();
          goToPrevious();
          break;
        case "ArrowRight":
          e.preventDefault();
          goToNext();
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          toggleZoom();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    isOpen,
    currentIndex,
    images.length,
    goToNext,
    goToPrevious,
    onClose,
    toggleZoom,
  ]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setIsZoomed(false);
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setIsZoomed(false);
  }, [images.length]);

  const toggleZoom = useCallback(() => {
    setIsZoomed((prev) => !prev);
  }, []);

  const handleImageError = useCallback((imageId: string) => {
    setImageError((prev) => ({ ...prev, [imageId]: true }));
  }, []);

  const handleDownload = useCallback(async (image: ImageItem) => {
    if (
      !image.url ||
      typeof image.url !== "string" ||
      image.url.trim() === ""
    ) {
      logger.warn("Cannot download image: invalid URL");
      return;
    }

    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download =
        image.originalName || image.filename || `image-${image._id}.jpg`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error("Failed to download image:", error);
    }
  }, []);

  if (!images || images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex];
  const hasMultipleImages = images.length > 1;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      hideCloseButton
      classNames={{
        base: cn("", className),
        backdrop: "bg-black bg-opacity-95 backdrop-blur-[0.4rem]",
      }}
      motionProps={{
        variants: {
          enter: {
            y: 0,
            opacity: 1,
            transition: {
              duration: 0.3,
              ease: "easeOut",
            },
          },
          exit: {
            y: -20,
            opacity: 0,
            transition: {
              duration: 0.2,
              ease: "easeIn",
            },
          },
        },
      }}
    >
      <ModalContent className="m-0 h-screen max-h-screen max-w-screen rounded-none p-0 bg-black bg-opacity-95 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between py-[2rem] px-[3rem] md:py-[1.5rem] md:px-[2rem] bg-black bg-opacity-80 border-b border-white border-opacity-10 flex-shrink-0">
          <div className="flex items-center gap-[2rem] md:gap-[1rem]">
            <span className="text-[1.6rem] font-semibold text-white font-geometria">
              {currentIndex + 1} / {images.length}
            </span>
            {currentImage.originalName && (
              <span className="text-[1.4rem] md:text-[1.2rem] font-normal text-white text-opacity-80 font-geometria max-w-[30rem] md:max-w-[15rem] overflow-hidden text-ellipsis whitespace-nowrap">
                {currentImage.originalName}
              </span>
            )}
          </div>

          <div className="flex items-center gap-[1rem] md:gap-[0.5rem]">
            <Button
              isIconOnly
              variant="light"
              size="sm"
              onPress={toggleZoom}
              className="bg-white bg-opacity-10 text-white border border-white border-opacity-20 transition-all duration-200 ease-out hover:bg-white hover:bg-opacity-20 hover:scale-105 w-[4rem] h-[4rem] min-w-[4rem]"
              aria-label={isZoomed ? "Уменьшить" : "Увеличить"}
            >
              {isZoomed ? <ZoomOut size={20} /> : <ZoomIn size={20} />}
            </Button>

            <Button
              isIconOnly
              variant="light"
              size="sm"
              onPress={() => handleDownload(currentImage)}
              className="bg-white bg-opacity-10 text-white border border-white border-opacity-20 transition-all duration-200 ease-out hover:bg-white hover:bg-opacity-20 hover:scale-105 w-[4rem] h-[4rem] min-w-[4rem]"
              aria-label="Скачать изображение"
            >
              <Download size={20} />
            </Button>

            <Button
              isIconOnly
              variant="light"
              size="sm"
              onPress={onClose}
              className="bg-[rgba(220,38,127,0.2)] border-[rgba(220,38,127,0.4)] text-white transition-all duration-200 ease-out hover:bg-[rgba(220,38,127,0.4)] w-[4rem] h-[4rem] min-w-[4rem]"
              aria-label="Закрыть"
            >
              <X size={24} />
            </Button>
          </div>
        </div>

        {/* Main image area */}
        <div className="relative flex-1 flex items-center justify-center overflow-hidden">
          {hasMultipleImages && (
            <Button
              isIconOnly
              variant="light"
              size="lg"
              onPress={goToPrevious}
              className="absolute top-1/2 -translate-y-1/2 z-10 bg-black bg-opacity-60 text-white border border-white border-opacity-20 w-[6rem] h-[6rem] md:w-[5rem] md:h-[5rem] transition-all duration-200 ease-out hover:bg-black hover:bg-opacity-80 hover:scale-110 left-[2rem] md:left-[1rem]"
              aria-label="Предыдущее изображение"
            >
              <ChevronLeft size={32} />
            </Button>
          )}

          <div
            className={cn(
              "relative w-[90%] h-[90%] max-w-[120rem] max-h-[80rem] transition-all duration-300 ease-out cursor-zoom-in",
              isZoomed && "w-full h-full max-w-none max-h-none cursor-zoom-out",
            )}
          >
            {imageError[currentImage._id] ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-[rgba(40,40,40,0.9)] rounded-[1rem] text-white text-center">
                <div className="text-[6rem] mb-[2rem] opacity-50">📷</div>
                <p className="text-[1.8rem] font-medium mb-[0.8rem] font-geometria">
                  Не удалось загрузить изображение
                </p>
                <p className="text-[1.4rem] font-normal opacity-70 font-geometria">
                  {currentImage.originalName || "image"}
                </p>
              </div>
            ) : (
              <Image
                src={currentImage.url}
                alt={currentImage.originalName || `Image ${currentIndex + 1}`}
                fill
                className={cn(
                  "transition-all duration-300 ease-out rounded-[1rem]",
                  isZoomed && "rounded-none",
                )}
                style={{
                  objectFit: isZoomed ? "contain" : "contain",
                }}
                onError={() => handleImageError(currentImage._id)}
                priority
                sizes="100vw"
                quality={90}
              />
            )}
          </div>

          {hasMultipleImages && (
            <Button
              isIconOnly
              variant="light"
              size="lg"
              onPress={goToNext}
              className="absolute top-1/2 -translate-y-1/2 z-10 bg-black bg-opacity-60 text-white border border-white border-opacity-20 w-[6rem] h-[6rem] md:w-[5rem] md:h-[5rem] transition-all duration-200 ease-out hover:bg-black hover:bg-opacity-80 hover:scale-110 right-[2rem] md:right-[1rem]"
              aria-label="Следующее изображение"
            >
              <ChevronRight size={32} />
            </Button>
          )}
        </div>

        {/* Thumbnail strip */}
        {hasMultipleImages && (
          <div className="bg-black bg-opacity-80 border-t border-white border-opacity-10 py-[2rem] md:py-[1.5rem] flex-shrink-0">
            <div className="flex gap-[1rem] md:gap-[0.8rem] justify-center overflow-x-auto px-[1rem]">
              {images.map((image, index) => (
                <button
                  key={image._id}
                  onClick={() => setCurrentIndex(index)}
                  className={cn(
                    "relative w-[8rem] h-[8rem] md:w-[6rem] md:h-[6rem] rounded-[0.8rem] md:rounded-[0.6rem] overflow-hidden border-2 border-transparent cursor-pointer transition-all duration-200 ease-out flex-shrink-0 bg-[rgba(40,40,40,0.9)] hover:border-white hover:border-opacity-40 hover:scale-105",
                    index === currentIndex && "border-red scale-110",
                  )}
                  aria-label={`Перейти к изображению ${index + 1}`}
                >
                  {imageError[image._id] ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-[rgba(40,40,40,0.9)] text-white text-opacity-50 text-[2rem]">
                      📷
                    </div>
                  ) : (
                    <Image
                      src={image.url}
                      alt={`Thumbnail ${index + 1}`}
                      fill
                      className="rounded-[0.6rem]"
                      style={{ objectFit: "cover" }}
                      onError={() => handleImageError(image._id)}
                      sizes="80px"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Touch/swipe indicators for mobile */}
        {hasMultipleImages && (
          <div className="absolute bottom-[2rem] left-1/2 -translate-x-1/2 bg-black bg-opacity-60 text-white text-opacity-80 py-[0.8rem] px-[1.6rem] rounded-[2rem] text-[1.2rem] font-normal font-geometria opacity-80 hidden md:block">
            <span className="whitespace-nowrap">
              Смахните влево или вправо для навигации
            </span>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ImageGalleryModal;
